import {AceLoggerService} from '@logging/ace-logger.service';
import {mergeMap, Observable, of, tap} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Task, TaskAlert, TaskAlertSchedule, TaskFunction, TaskId, TaskProperties, TaskSensor} from '@shared/services/api/task.interface';
import {BaseService} from '@shared/services/api/base-service';
import {PagedModel} from '@shared/api/model/paged-model';
import {AceLogger} from '@logging/ace-logger';
import {APIPageInfo, GRAPHQL_TYPES, HttpUtilityService} from '@app/shared/services/api/http-utility-service';
import * as gql from 'gql-query-builder';
import {ClassTransformer, Transform, Type} from "class-transformer";
import moment, {Moment} from 'moment-timezone';
import {Template} from "@app/graphql-typings/graphql-tasks";
import Fields from "gql-query-builder/build/Fields";
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import IQueryBuilderOptions from 'gql-query-builder/build/IQueryBuilderOptions';
import {MomentUtil} from '@app/shared/util/datetime/moment-util';
import {objectToHttpParams} from '@app/shared/util/angular/angular-utils';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {DateTimeRangeTuple} from '@app/graphql-typings/graphql';
import {TemplateCategoryId} from '../template-categories/models/template-category';
import {SiteId} from '@app/shared/services/api/access-sites.interface';

export enum EquipmentScoringValue {
  OPT_OUT = 0,
  OPT_IN = 1
}

export enum TaskType {
  BATCH = 'BATCH',
  SCHEDULED = 'SCHEDULED'
}

export interface TaskScopeOptions {
  getAlertSchedule?: boolean;
}

export interface TaskState {
  task_id: number;
  task_state: boolean;
}

export interface TaskUpdateActionData {
  task_id?: number;
  task_state?: boolean;
  score_multiplier?: number;
  trigger_alert?: boolean;
}

export interface TaskDeleteResponse {
  task_id: number;
  deleted_at: string;
}

export interface TasksSearchTerm {
  site_id?: number;
  site_ids?: number[];
  fav_id?: number;
  fav_ids?: number[];
  equipment_id?: number;
  equipment_ids?: number[];
  template_id?: string;
  template_ids?: string[];
  task_id?: number;
  task_ids?: number[];
  primary_metadata_type_ids?: number[];
  score_multiplier_gt?: number;
  trigger_alert?: boolean;
}

export enum TaskStateAction {
  START = 'start',
  STOP = 'stop'
}

enum TASK_OPERATION {
  TASK = 'task',
  TASKS = 'tasks',
  TASK_ALERTS_GRAPHQL_OPERATION = 'task_alerts'
}

export interface TaskSearchParams {
  task_id?: number;
  task_ids?: number[];
  metadata_type_ids?: number[];
  primary_metadata_type_ids?: number[];
  equipment_ids?: number[];
  site_id?: number;
  template_id?: string;
  template_ids?: string[];
  task_state?: boolean;
  fav_id?: number;
  fav_ids?: number[];
  match_exact?: boolean;
  equipment_id_tuples?: number[][];
  score_multiplier?: number;
  trigger_alert?: boolean;
  template_category_id?: TemplateCategoryId;
  template_category_ids?: TemplateCategoryId[];
  score_multiplier_gt?: number;
  no_sensors?: boolean;
  no_functions?: boolean;
}

export interface TaskAlertSearchParams {
  site_ids?: number[]
  primary_metadata_type_ids?: number[];
  score_multiplier_gt?: number;
  task_ids?: number[];
  in_alert_ts_range?: DateTimeRangeTuple;
  has_executed?: boolean;
}

export enum TasksAggregationEntity {
  SITE = 'site',
  METADATA_TYPE = 'metadata_type',
  EQUIPMENT = 'equipment',
  ALL = 'all'
}

export interface TasksAggregationSearchTerm {
  site_id?: number;
  site_ids?: number[]
  equipment_id?: number;
  equipment_ids?: number[];
  primary_metadata_type_id?: number;
  primary_metadata_type_ids?: number[];
  primary_equipment_only?: boolean;
  aggregate_entity: TasksAggregationEntity;
}

export interface TaskCountAggregation {
  site_id?: number;
  metadata_type_id?: number;
  equipment_id?: number;
  count: number;
}

export interface TasksAggregationResponse {
  task_count_aggregation: TaskCountAggregation[];
}

export type TaskAlertField = 'start_ts' | 'end_ts' | 'task_alert_id' | 'task_id';

class TaskFunctionImpl implements TaskFunction {
  function_id: number;
  /* Addresses a tasks GraphQL bug where thresholds are received as a string type instead of a number type*/
  @Transform(({value}: { value: string | null }) => value ? Number(value) : value)
  threshold: number;
  name:  string;
  formula: string;
}

class TaskImpl implements Task {
  task_id: TaskId;
  task_type: TaskType;
  task_state: boolean;
  is_active: boolean;
  properties: TaskProperties;
  @Type(() => TaskFunctionImpl)
  functions: TaskFunction[];
  sensors: TaskSensor[];
  alert_schedule: TaskAlertSchedule;
  site_id: number;
  template: Template;
  template_id: string;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at?: Moment;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  created_at?: Moment;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  deleted_at: Moment;
  score_multiplier: number;
  trigger_alert: boolean;
}

@Injectable()
export class RulesService extends BaseService  {
  private readonly TASK_GRAPHQL_VARIABLES = {
    task_id: {type: GRAPHQL_TYPES.INTEGER},
    task_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
    fav_id: {type: GRAPHQL_TYPES.LONG},
    fav_ids: {type: GRAPHQL_TYPES.LONG, list: true},
    site_id: {type: GRAPHQL_TYPES.INTEGER},
    site_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
    equipment_id: {type: GRAPHQL_TYPES.LONG},
    equipment_ids: {type: GRAPHQL_TYPES.LONG, list: true},
    metadata_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
    primary_metadata_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
    template_id: {type: GRAPHQL_TYPES.UUID},
    template_ids: {type: GRAPHQL_TYPES.UUID, list: true},
    task_state: {type: GRAPHQL_TYPES.BOOLEAN},
    match_exact: {type: GRAPHQL_TYPES.BOOLEAN},
    equipment_id_tuples: { type: GRAPHQL_TYPES.LONG_ARRAY, list: true },
    score_multiplier: {type: GRAPHQL_TYPES.DOUBLE},
    trigger_alert: {type: GRAPHQL_TYPES.BOOLEAN},
    template_category_id: {type: GRAPHQL_TYPES.INTEGER},
    template_category_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
    score_multiplier_gt: {type: GRAPHQL_TYPES.DOUBLE},
    no_sensors: {type: GRAPHQL_TYPES.BOOLEAN},
    no_functions: {type: GRAPHQL_TYPES.BOOLEAN},
    start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
    limit: {type: GRAPHQL_TYPES.INTEGER, required: true},
  };
  private readonly TASK_ALERT_SCHEDULE_FIELDS =
    ['timezone', 'in_schedule',
      {'monday': ['enabled', 'startTime', 'endTime']},
      {'tuesday': ['enabled', 'startTime', 'endTime']},
      {'wednesday': ['enabled', 'startTime', 'endTime']},
      {'thursday': ['enabled', 'startTime', 'endTime']},
      {'friday': ['enabled', 'startTime', 'endTime']},
      {'saturday': ['enabled', 'startTime', 'endTime']},
      {'sunday': ['enabled', 'startTime', 'endTime']}];
  private readonly TASK_SENSOR_FIELDS = ['fav_id', 'equipment_id', 'metadata_id', 'name', 'sensor_id'];
  private readonly TASK_FUNCTION_FIELDS = ['function_id', 'threshold', 'formula', 'name'];
  private readonly TASK_PROPERTIES_FIELDS = ['faults_per_alert', 'nonfaults_per_recovery'];
  private readonly TASK_TEMPLATE_FIELDS = ['template_id', 'template_name', 'template_category_id', 'primary_metadata_type_ids', 'priority_id', {properties: ['rule_name']}];
  private readonly TASK_ALL_FIELDS =
    ['task_id', 'task_type', 'task_state', 'site_id', 'score_multiplier', 'trigger_alert', 'canonical_priority_id',
      {template: this.TASK_TEMPLATE_FIELDS},
      {sensors: this.TASK_SENSOR_FIELDS},
      {properties: this.TASK_PROPERTIES_FIELDS},
      {functions: this.TASK_FUNCTION_FIELDS},
      {alert_schedule: this.TASK_ALERT_SCHEDULE_FIELDS}];
  private readonly TASK_BASE_FIELDS =
    ['task_id', 'task_name', 'task_state',
      {template: this.TASK_TEMPLATE_FIELDS},
      {sensors: this.TASK_SENSOR_FIELDS}];
  private readonly TASK_ALERT_FIELDS: TaskAlertField[] = ['start_ts', 'end_ts', 'task_alert_id', 'task_id'];
  private readonly modelMapping = {Task: TaskImpl};
  private readonly RULE_PARTITION_SIZE = 400;
  private readonly RULE_UPDATE_BATCH_SIZE = 100;
  private readonly TASKS_URL_PART = '/' + TASK_OPERATION.TASKS;
  private readonly logger: AceLogger;
  private headers: HttpHeaders = this.getHeaders();

  readonly BULK_ACTION_MAX_TASKS = 10000;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private classTransformer: ClassTransformer,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(RulesService.name);
  }

  updateTask(task: Task): Observable<Task> {
    const options = {headers: this.headers};
    return this.http.put(this.getGateway()  + this.TASKS_URL_PART + '/' + task.task_id, task, options).pipe(
      map(response => {
        const data = this.httpUtilityService.extractResponseData(response, TASK_OPERATION.TASK);
        return this.classTransformer.plainToInstance(this.modelMapping.Task, data);
      }));
  }

  bulkUpsertTasks(tasks: Task[], onProgressFn?: (progress: number) => void): Observable<Task[]> {
    let upsertedTaskCount = 0;
    return this.httpUtilityService.requestPartitioned((taskBatch: Task[]) => {
      return this.upsertTasks(taskBatch).pipe(tap(updatedTaskBatch => {
        upsertedTaskCount += updatedTaskBatch.length;
        onProgressFn?.(upsertedTaskCount / tasks.length * 100);
      }));
    }, tasks, this.RULE_UPDATE_BATCH_SIZE);
  }

  getTasks(taskSearchTerm: TasksSearchTerm, pageInfo: {first: number, rows: number}):  Observable<PagedModel<Task[]>> {
    const tasksQuery = {
      operation: TASK_OPERATION.TASKS,
      fields: [
        'task_id',
        'task_name',
        'task_state',
        {template: this.TASK_TEMPLATE_FIELDS},
        {sensors: ['equipment_id', 'fav_id', 'name']},
        {alert_schedule: this.TASK_ALERT_SCHEDULE_FIELDS}
      ],
      variables: this.TASK_GRAPHQL_VARIABLES
    };
    const query = gql.query(tasksQuery);
    const variables = {...taskSearchTerm, ... {start_index: pageInfo.first, limit: pageInfo.rows}};
    return this.httpUtilityService.tasksGraphQL<Task[]>(query, tasksQuery.operation, variables).pipe(map((result: PagedModel<Task[]>) => {
      return {
        data: this.classTransformer.plainToInstance(this.modelMapping.Task, result.data),
        count: result.count
      };
    }));
  }

  getTasksCount(taskSearchTerm: TasksAggregationSearchTerm): Observable<TaskCountAggregation[]> {
    const endpoint = this.getGateway() + this.TASKS_URL_PART + '/aggregation';
    return this.http.get<APIResponse<TasksAggregationResponse>>(endpoint, {params: objectToHttpParams(taskSearchTerm)})
      .pipe(map(response => response[this.DATA_PREFIX].task_count_aggregation));
  }

  getTasksCountByTemplateId(templateIds: string[]): Observable<number> {
    return this.getTasks({template_ids: templateIds}, {first: 0, rows: 1}).pipe(map((pagedTasks:PagedModel<Task[]>) => pagedTasks.count));
  }

  // Counts the tasks that feed the site's equipment scoring (running, active, score_multiplier > 0), i.e. one row per
  // task in a pass-rate aggregation. Sensors and functions are suppressed so the backend skips their per-task joins.
  getScoringTaskCountBySiteId(siteId: SiteId): Observable<number> {
    const searchParams: TaskSearchParams = {site_id: siteId, task_state: true, score_multiplier_gt: 0, no_sensors: true, no_functions: true};
    return this.getTasksPagedModel(searchParams, ['task_id'], 0, 1).pipe(map(({count}: PagedModel<Task[]>) => count));
  }

  startTask(taskId): Observable<TaskState> {
    return this.actionTask(taskId, TaskStateAction.START);
  }

  stopTask(taskId): Observable<TaskState> {
    return this.actionTask(taskId, TaskStateAction.STOP);
  }

  private actionTask(taskId, action: TaskStateAction): Observable<TaskState>  {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + this.TASKS_URL_PART + '/' + taskId + '/' + action, {}, options).pipe(
      map(response => this.httpUtilityService.extractResponseData(response, TASK_OPERATION.TASK)));
  }

  startTasks(taskIds: number[]): Observable<TaskState[]> {
    return this.actionTasks(taskIds, TaskStateAction.START);
  }

  stopTasks(taskIds: number[]): Observable<TaskState[]> {
    return this.actionTasks(taskIds, TaskStateAction.STOP);
  }

  private upsertTasks(tasks: Task[]): Observable<Task[]> {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + this.TASKS_URL_PART, tasks, options).pipe(
      map(response => {
        const data: Task[] = this.httpUtilityService.extractResponseData(response, TASK_OPERATION.TASKS);
        return this.classTransformer.plainToInstance(this.modelMapping.Task, data);
      }));
  }

  private actionTasks(taskIds: number[], action: TaskStateAction): Observable<TaskState[]> {
    const wrapper = (taskIds: number[]): Observable<TaskState[]> => {
      const options = {headers: this.headers};
      const body = {'task_ids': taskIds};
      return this.http.post(this.getGateway() + this.TASKS_URL_PART + '/' + action, body, options)
        .pipe(map((response: APIResponse<TaskState[]>) => this.httpUtilityService.extractResponseData(response, TASK_OPERATION.TASKS)));
    };
    return this.httpUtilityService.requestPartitioned(wrapper, taskIds, this.RULE_PARTITION_SIZE);
  }

  private patchTaskFieldById(taskId: TaskId, taskPatchBody: Partial<Task>): Observable<Task> {
    return this.getTaskById(taskId).pipe(mergeMap(task => this.updateTask({...task, ...taskPatchBody})));
  }

  isTaskRunningStateValid(scoreMultiplier: number, triggerAlert: boolean): boolean {
    return scoreMultiplier > EquipmentScoringValue.OPT_OUT || triggerAlert;
  }

  updateTaskScore(taskId: TaskId, scoreMultiplier: number): Observable<Task> {
    return this.patchTaskFieldById(taskId, {score_multiplier: scoreMultiplier});
  }

  updateTaskTriggerAlert(taskId: TaskId, triggerAlert: boolean): Observable<Task> {
    return this.patchTaskFieldById(taskId, {trigger_alert: triggerAlert});
  }

  deleteTask(taskId): Observable<TaskDeleteResponse> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + this.TASKS_URL_PART + '/' + taskId,  options).pipe(
      map(response => this.httpUtilityService.extractResponseData(response, TASK_OPERATION.TASK)));
  }

  deleteTasks(taskIds: number[]): Observable<Object> {
    const wrapper = (taskIds: number[]): Observable<Object[]> => {
      const params = {task_ids: taskIds.map(id => id.toString())};
      const options = {headers: this.headers, params: params};
      return this.http.delete<Object[]>(this.getGateway() + this.TASKS_URL_PART, options);
    };
    return this.httpUtilityService.requestPartitioned(wrapper, taskIds, this.RULE_PARTITION_SIZE);
  }

  getTasksSummary(searchParams: TaskSearchParams, offset: number, limit: number): Observable<PagedModel<Task[]>> {
    const fields: Fields = ['task_id', 'task_state', 'site_id', 'score_multiplier', 'trigger_alert', 'created_at', 'last_modified_at',
      {template: this.TASK_TEMPLATE_FIELDS},
      {sensors: this.TASK_SENSOR_FIELDS}];
    return this.getTasksPagedModel(searchParams, fields, offset, limit);
  }

  public getAllTasksSummary(searchParams: TaskSearchParams, fetchPageSize: number = this.RULE_PARTITION_SIZE): Observable<Task[]> {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo: APIPageInfo): Observable<PagedModel<Task[]>> => this.getTasksSummary(searchParams, pageInfo.first, pageInfo.rows),
      {first: 0, rows: fetchPageSize});
  }

  getAllTasksUpdateActionsFields(searchParams: TaskSearchParams): Observable<TaskUpdateActionData[]> {
    return this.getTasksWithFields(searchParams, ['task_id', 'task_state', 'score_multiplier', 'trigger_alert']);
  }

  getTaskById(taskId: number): Observable<Task> {
    const fields: Fields = this.TASK_ALL_FIELDS;
    return this.getTasksPagedModel({task_id: taskId}, fields, 0,  1)
      .pipe(map(({data: tasks}: PagedModel<Task[]>) => CollectionUtils.only(tasks)));
  }

  getAllTasksById(taskIds: number[], progressCallback?: (a: number) => void): Observable<Task[]> {
    const fields: Fields = this.TASK_ALL_FIELDS;
    let completedSoFar = 0;
    const wrapper = (partitionedTaskIds: number[]) => this.getTasksPagedModel( {task_ids: partitionedTaskIds}, fields, 0,  this.RULE_PARTITION_SIZE)
      .pipe(map(({data: data}: PagedModel<Task[]>) => {
        if (typeof progressCallback === 'function') {
          completedSoFar += data.length;
          progressCallback(completedSoFar);
        }
        return data;
      }));
    return this.httpUtilityService.requestPartitioned(wrapper, taskIds, this.RULE_PARTITION_SIZE);
  }

  getTasksForEquipment(equipmentIds: number[], scopeOptions: TaskScopeOptions = {}): Observable<Task[]> {
    const wrapper = (partitionedEquipmentIds: number[]) =>
      this.getTasksWithFields({equipment_ids: partitionedEquipmentIds}, this.buildTaskFields(scopeOptions));
    return this.httpUtilityService.requestPartitioned(wrapper, equipmentIds, this.RULE_PARTITION_SIZE);
  }

  private getTasksPagedModel(searchParams: TaskSearchParams, fields: Fields, offset: number, limit: number): Observable<PagedModel<Task[]>> {
    const OPERATION = TASK_OPERATION.TASKS;
    const query = gql.query({
      operation: OPERATION,
      fields: fields,
      variables: this.TASK_GRAPHQL_VARIABLES
    });
    const modifiedSearchParams = {...searchParams};
    if (modifiedSearchParams.equipment_ids?.length) {
      delete modifiedSearchParams.metadata_type_ids;
    }
    if ((modifiedSearchParams.template_ids?.length || modifiedSearchParams.template_id)) {
      delete modifiedSearchParams.template_category_id;
      delete modifiedSearchParams.template_category_ids;
    }
    return this.httpUtilityService.tasksGraphQL<Task[]>(query, OPERATION, Object.assign({start_index: offset, limit: limit}, modifiedSearchParams))
      .pipe(map((result: PagedModel<Task[]>) => {
        return {
          data: this.classTransformer.plainToInstance(this.modelMapping.Task, result.data),
          count: result.count
        };
      }));
  }


  getDuplicateTasks(siteId: number, templateId: string, favIds: number[], limit: number): Observable<number[]> {
    if (Number.isInteger(siteId) && favIds.length !== 0) {
      const searchParams: TaskSearchParams = {site_id: siteId, template_id: templateId, fav_ids: favIds, match_exact: true};
      return this.getTasksPagedModel(searchParams, ['task_id'], 0, limit)
        .pipe(map(({data: tasks}: PagedModel<Task[]>) => tasks.map((task: Task) => task.task_id)));
    } else {
      return of([]);
    }
  }

  public getTasksById(taskIds: number[], scopeOptions: TaskScopeOptions = {}): Observable<Task[]> {
    const fields: Fields = this.buildTaskFields(scopeOptions);
    const wrapper = (partitionedTaskIds: number[]) => this.getTasksPagedModel( {task_ids: partitionedTaskIds}, fields, 0,  this.RULE_PARTITION_SIZE)
      .pipe(map(({data: tasks}: PagedModel<Task[]>) => tasks));
    return this.httpUtilityService.requestPartitioned(wrapper, taskIds, this.RULE_PARTITION_SIZE);
  }

  private buildTaskFields(scopeOptions: TaskScopeOptions): Fields {
    return [
      ...this.TASK_BASE_FIELDS,
      ...(scopeOptions?.getAlertSchedule ? [{alert_schedule: this.TASK_ALERT_SCHEDULE_FIELDS}] : [])
    ];
  }

  private getTasksWithFields(searchParams: TaskSearchParams, fields: Fields, fetchPageSize: number = this.RULE_PARTITION_SIZE): Observable<Task[]> {
    const taskPageWrapper = (pageInfo: APIPageInfo): Observable<PagedModel<Task[]>> => this.getTasksPagedModel(searchParams, fields, pageInfo.first, pageInfo.rows);
    return this.httpUtilityService.pageUntilComplete(taskPageWrapper, {first: 0, rows: fetchPageSize});
  }

  getTaskAlerts(
    taskAlertSearchParams: TaskAlertSearchParams,
    offset: number,
    limit: number,
    fields: TaskAlertField[] = this.TASK_ALERT_FIELDS,
  ): Observable<PagedModel<TaskAlert[]>> {
    const query: IQueryBuilderOptions = {
      operation: TASK_OPERATION.TASK_ALERTS_GRAPHQL_OPERATION,
      fields,
      variables: {
        site_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        primary_metadata_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        score_multiplier_gt: {type: GRAPHQL_TYPES.DOUBLE},
        task_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        in_alert_ts_range: {type: GRAPHQL_TYPES.INTERVAL},
        has_executed: {type: GRAPHQL_TYPES.BOOLEAN},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };

    const variables = {
      start_index: offset,
      limit,
      ...taskAlertSearchParams,
      ...taskAlertSearchParams.in_alert_ts_range && {
        in_alert_ts_range: MomentUtil.rangeToInterval(taskAlertSearchParams.in_alert_ts_range),
      },
    };
    return this.httpUtilityService.tasksGraphQL<TaskAlert[]>(gql.query(query), TASK_OPERATION.TASK_ALERTS_GRAPHQL_OPERATION, variables);
  }
}
